/**
 * Report-scoped language: the report can be read in EN or AR independently of the portal locale (language toggle),
 * so labels and finding text inside the report use this translator, not the shell's.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { createTranslator, dirFor, formatDateTime, formatNumber, type Translate } from '@qm/i18n';
import type { LocalizedText } from '@qm/contracts/audit-analysis';
import { textFor, type Loc } from './model';

export interface ReportText {
  t: Translate; loc: Loc; dir: 'ltr' | 'rtl';
  num: (n: number) => string; pct: (ratio: number) => string;
  date: (iso: string | null | undefined) => string; dateTime: (iso: string | null | undefined) => string;
}

const Ctx = createContext<ReportText | null>(null);

export function ReportTextProvider({ loc, timeZone, children }: { loc: Loc; timeZone?: string; children: ReactNode }) {
  const value = useMemo<ReportText>(() => ({
    t: createTranslator(loc), loc, dir: dirFor(loc),
    num: (n) => formatNumber(loc, n),
    pct: (r) => formatNumber(loc, r, { style: 'percent', maximumFractionDigits: 0 }),
    date: (iso) => (iso ? formatDateTime(loc, iso, { timeZone, style: 'date' }) : ''),
    dateTime: (iso) => (iso ? formatDateTime(loc, iso, { timeZone }) : ''),
  }), [loc, timeZone]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useReportText(): ReportText {
  const v = useContext(Ctx);
  if (!v) throw new Error('useReportText outside ReportTextProvider');
  return v;
}

/** Finding text in the report language; an English fallback inside an Arabic report carries lang="en" dir="auto". */
export function L({ v, as: Tag = 'span', className }: { v: LocalizedText | null | undefined; as?: 'span' | 'p' | 'b' | 'strong'; className?: string }) {
  const { loc } = useReportText();
  const x = textFor(v, loc);
  if (!x) return null;
  const foreign = x.lang !== loc;
  return <Tag className={className} lang={foreign ? x.lang : undefined} dir={foreign ? 'auto' : undefined}>{x.text}</Tag>;
}
